import { gql, TypedDocumentNode } from '@apollo/client';
import {
  sysFragment,
  pageCommunityDetailMetadataFragment,
  pageCommunityDetailHyperlinkFragment,
  contentfulMetadataFragment,
  heroLargeFragment,
  mediaAssetFragment,
  mediaImageFragment,
  mediaVideoFragment,
  alternatingGalleryBlockFragment,
  alternatingTextBlockFragment,
  CTAFragment,
  galleryFragment,
  hyperlinkFragment,
  matterportBlockFragment,
  pageCommunityNewsHyperlinkFragment,
  pageCommunityEventHyperlinkFragment,
  pageCommunityNewsMetadataFragment,
  pageCommunityEventMetadataFragment,
  pageCommunityPropertyDetailHyperlinkFragment,
  pageCommunityPropertyDetailMetadataFragment,
  pageCommunityStandardHyperlinkFragment,
  pageCommunityStandardMetadataFragment,
  pageNationalNewsHyperlinkFragment,
  pageNationalNewsMetadataFragment,
  pageNationalStandardHyperlinkFragment,
  pageNationalStandardMetadataFragment,
  productPromotionBlockFragment,
  sectionTitleBlockFragment,
  contentBlocksPart1CollectionCommunityDetailFragment,
  contentBlocksPart2CollectionCommunityDetailFragment,
  discoverBlockFragment,
  genericTextBlockFragment,
  videoBlockFragment,
  twoColumnBlockFragment,
  staticBlockFragment,
  locationFragment,
  accordionBlockFragment,
  tileContainerBlockFragment,
  carouselBlockFragment,
  featureBlockFragment,
  featureNumberBlockFragment,
  imageCardBlockFragment,
  listBlockFragment,
  quoteBlockFragment,
  salesBannerBlockFragment,
  textCardBlockFragment,
  threeCardBlockFragment,
  contentBlocksPart3CollectionCommunityDetailFragment,
  contentBlocksPart4CollectionCommunityDetailFragment
} from '@src/graphql/fragments';

export const pageCommunityDetailQuery = gql`
  query pageCommunityDetail($slug: String, $id: String, $preview: Boolean) {
    page: pageCommunityDetailCollection(
      where: { slug: $slug, sys: { id: $id } }
      preview: $preview
      limit: 1
    ) {
      items {
        sys {
          publishedAt
          ...sys
        }
        title
        slug
        introText
        communitySuburb {
          ...location
        }
        state {
          slug
          stateterritoryLabel
        }
        communityAddress
        latLon {
          lat
          lon
        }
        seoTitle
        seoDescription
        noIndex
        noFollow
        hero {
          ...heroLarge
        }
        displayTourBookingButton
        introText
        isLiveChatEnabled
        discoverBlock {
          ...discoverBlock
        }
        disclaimer {
          ...genericTextBlock
        }
        contactSalesRepresentative {
          image {
            ...mediaImage
          }
          name
          email
          phoneNumber
        }
      }
    }

    pagePath: pageCommunityDetailCollection(
      where: { slug: $slug, sys: { id: $id } }
      limit: 1
      preview: $preview
    ) {
      items {
        ...pageCommunityDetailHyperlink
      }
    }
  }

  ${sysFragment}
  ${pageCommunityDetailMetadataFragment}
  ${pageCommunityDetailHyperlinkFragment}
  ${contentfulMetadataFragment}

  ${mediaAssetFragment}
  ${mediaImageFragment}
  ${mediaVideoFragment}
  ${CTAFragment}

  ${locationFragment}
  ${genericTextBlockFragment}
  ${discoverBlockFragment}
  ${heroLargeFragment}
  ${galleryFragment}

  ${hyperlinkFragment}

  ${pageNationalStandardMetadataFragment}
  ${pageNationalNewsMetadataFragment}
  ${pageCommunityDetailMetadataFragment}
  ${pageCommunityStandardMetadataFragment}
  ${pageCommunityNewsMetadataFragment}
  ${pageCommunityEventMetadataFragment}
  ${pageCommunityPropertyDetailMetadataFragment}

  ${pageNationalStandardHyperlinkFragment}
  ${pageNationalNewsHyperlinkFragment}
  ${pageCommunityDetailHyperlinkFragment}
  ${pageCommunityStandardHyperlinkFragment}
  ${pageCommunityNewsHyperlinkFragment}
  ${pageCommunityEventHyperlinkFragment}
  ${pageCommunityPropertyDetailHyperlinkFragment}
` as TypedDocumentNode;

export const mapCommunityDetail = gql`
  query mapCommunityDetail($stateSlug: String, $preview: Boolean) {
    stateCollection(preview: $preview, limit: 8) {
      items {
        slug
        __typename
        locationInfoBlock {
          ...sectionTitleBlock
        }
        regionsCollection(limit: 10) {
          items {
            name
            slug
            locationInfoBlock {
              ...sectionTitleBlock
            }
          }
        }
      }
      __typename
    }
    mapItems: pageCommunityDetailCollection(
      where: { state: { slug: $stateSlug } }
      order: title_ASC
      preview: $preview
      limit: 75
    ) {
      total
      items {
        mapAssets: hero {
          mediaItem {
            ... on MediaImage {
              cloudinarySource
              __typename
            }
            ... on MediaVideo {
              coverImage
              __typename
            }
            __typename
          }
          __typename
        }
        ...pageCommunityDetailHyperlink
        communitySuburb {
          ...location
        }
        findYourCommunityCardSubtext
        latLon {
          lat
          lon
          __typename
        }
        __typename
      }
      __typename
    }
  }

  ${sysFragment}
  ${locationFragment}

  ${mediaVideoFragment}
  ${mediaAssetFragment}
  ${hyperlinkFragment}
  ${CTAFragment}

  ${sectionTitleBlockFragment}

  ${pageNationalStandardMetadataFragment}
  ${pageNationalNewsMetadataFragment}
  ${pageCommunityDetailMetadataFragment}
  ${pageCommunityStandardMetadataFragment}
  ${pageCommunityNewsMetadataFragment}
  ${pageCommunityEventMetadataFragment}
  ${pageCommunityPropertyDetailMetadataFragment}

  ${pageNationalNewsHyperlinkFragment}
  ${pageNationalStandardHyperlinkFragment}
  ${pageCommunityDetailHyperlinkFragment}
  ${pageCommunityStandardHyperlinkFragment}
  ${pageCommunityNewsHyperlinkFragment}
  ${pageCommunityEventHyperlinkFragment}
  ${pageCommunityPropertyDetailHyperlinkFragment}

  ${contentfulMetadataFragment}
` as TypedDocumentNode;

export const pageCommunityDetailPart1ContentBlocksQuery = gql`
  query pageCommunityDetailPart1ContentBlocks($id: String, $preview: Boolean) {
    pageContentBlocks: pageCommunityDetailCollection(
      where: { sys: { id: $id } }
      preview: $preview
      limit: 1
    ) {
      items {
        ...contentBlocksPart1CollectionCommunityDetail
      }
    }
  }

  ${sysFragment}
  ${contentBlocksPart1CollectionCommunityDetailFragment}

  ${hyperlinkFragment}
  ${contentfulMetadataFragment}

  ${galleryFragment}
  ${CTAFragment}

  ${mediaAssetFragment}
  ${mediaImageFragment}
  ${mediaVideoFragment}

  ${alternatingGalleryBlockFragment}
  ${alternatingTextBlockFragment}
  ${matterportBlockFragment}
  ${productPromotionBlockFragment}
  ${sectionTitleBlockFragment}
  ${videoBlockFragment}
  ${twoColumnBlockFragment}
  ${staticBlockFragment}

  ${pageNationalStandardMetadataFragment}
  ${pageNationalNewsMetadataFragment}
  ${pageCommunityDetailMetadataFragment}
  ${pageCommunityStandardMetadataFragment}
  ${pageCommunityNewsMetadataFragment}
  ${pageCommunityEventMetadataFragment}
  ${pageCommunityPropertyDetailMetadataFragment}

  ${pageNationalStandardHyperlinkFragment}
  ${pageNationalNewsHyperlinkFragment}
  ${pageCommunityDetailHyperlinkFragment}
  ${pageCommunityStandardHyperlinkFragment}
  ${pageCommunityNewsHyperlinkFragment}
  ${pageCommunityEventHyperlinkFragment}
  ${pageCommunityPropertyDetailHyperlinkFragment}
` as TypedDocumentNode;

export const pageCommunityDetailPart2ContentBlocksQuery = gql`
  query pageCommunityDetailPart2ContentBlocks($id: String, $preview: Boolean) {
    pageContentBlocks: pageCommunityDetailCollection(
      where: { sys: { id: $id } }
      preview: $preview
      limit: 1
    ) {
      items {
        ...contentBlocksPart2CollectionCommunityDetail
      }
    }
  }

  ${sysFragment}
  ${contentBlocksPart2CollectionCommunityDetailFragment}

  ${mediaAssetFragment}
  ${mediaImageFragment}
  ${mediaVideoFragment}
  ${CTAFragment}

  ${accordionBlockFragment}
  ${carouselBlockFragment}
  ${discoverBlockFragment}
  ${featureBlockFragment}
  ${featureNumberBlockFragment}

  ${hyperlinkFragment}
  ${contentfulMetadataFragment}

  ${pageNationalStandardMetadataFragment}
  ${pageNationalNewsMetadataFragment}
  ${pageCommunityDetailMetadataFragment}
  ${pageCommunityStandardMetadataFragment}
  ${pageCommunityNewsMetadataFragment}
  ${pageCommunityEventMetadataFragment}
  ${pageCommunityPropertyDetailMetadataFragment}

  ${pageNationalStandardHyperlinkFragment}
  ${pageNationalNewsHyperlinkFragment}
  ${pageCommunityDetailHyperlinkFragment}
  ${pageCommunityStandardHyperlinkFragment}
  ${pageCommunityNewsHyperlinkFragment}
  ${pageCommunityEventHyperlinkFragment}
  ${pageCommunityPropertyDetailHyperlinkFragment}
` as TypedDocumentNode;

export const pageCommunityDetailPart3ContentBlocksQuery = gql`
  query pageCommunityDetailPart3ContentBlocks($id: String, $preview: Boolean) {
    pageContentBlocks: pageCommunityDetailCollection(
      where: { sys: { id: $id } }
      preview: $preview
      limit: 1
    ) {
      items {
        ...contentBlocksPart3CollectionCommunityDetail
      }
    }
  }

  ${sysFragment}
  ${contentBlocksPart3CollectionCommunityDetailFragment}

  ${mediaAssetFragment}
  ${mediaImageFragment}
  ${mediaVideoFragment}
  ${CTAFragment}

  ${imageCardBlockFragment}
  ${listBlockFragment}
  ${quoteBlockFragment}
  ${salesBannerBlockFragment}

  ${hyperlinkFragment}
  ${contentfulMetadataFragment}

  ${pageNationalStandardMetadataFragment}
  ${pageNationalNewsMetadataFragment}
  ${pageCommunityDetailMetadataFragment}
  ${pageCommunityStandardMetadataFragment}
  ${pageCommunityNewsMetadataFragment}
  ${pageCommunityEventMetadataFragment}
  ${pageCommunityPropertyDetailMetadataFragment}

  ${pageNationalStandardHyperlinkFragment}
  ${pageNationalNewsHyperlinkFragment}
  ${pageCommunityDetailHyperlinkFragment}
  ${pageCommunityStandardHyperlinkFragment}
  ${pageCommunityNewsHyperlinkFragment}
  ${pageCommunityEventHyperlinkFragment}
  ${pageCommunityPropertyDetailHyperlinkFragment}
` as TypedDocumentNode;

export const pageCommunityDetailPart4ContentBlocksQuery = gql`
  query pageCommunityDetailPart4ContentBlocks($id: String, $preview: Boolean) {
    pageContentBlocks: pageCommunityDetailCollection(
      where: { sys: { id: $id } }
      preview: $preview
      limit: 1
    ) {
      items {
        ...contentBlocksPart4CollectionCommunityDetail
      }
    }
  }

  ${sysFragment}
  ${contentBlocksPart4CollectionCommunityDetailFragment}

  ${mediaAssetFragment}
  ${mediaImageFragment}
  ${mediaVideoFragment}
  ${CTAFragment}

  ${textCardBlockFragment}
  ${threeCardBlockFragment}
  ${tileContainerBlockFragment}

  ${hyperlinkFragment}
  ${contentfulMetadataFragment}

  ${pageNationalStandardMetadataFragment}
  ${pageNationalNewsMetadataFragment}
  ${pageCommunityDetailMetadataFragment}
  ${pageCommunityStandardMetadataFragment}
  ${pageCommunityNewsMetadataFragment}
  ${pageCommunityEventMetadataFragment}
  ${pageCommunityPropertyDetailMetadataFragment}

  ${pageNationalStandardHyperlinkFragment}
  ${pageNationalNewsHyperlinkFragment}
  ${pageCommunityDetailHyperlinkFragment}
  ${pageCommunityStandardHyperlinkFragment}
  ${pageCommunityNewsHyperlinkFragment}
  ${pageCommunityEventHyperlinkFragment}
  ${pageCommunityPropertyDetailHyperlinkFragment}
` as TypedDocumentNode;
